/**
 * <pcar-grid-tabs> — "Shop Our Favorites" tabbed product grid
 * (sections/featured-carousel.liquid). Ported from swisswatches.com's
 * sw-inventory-tabs: ARIA tabs in a sticky pill bar over one grid, per-tab
 * card cache, skeletons while loading. Each tab shows one page: two rows on
 * desktop, the configured count (in whole rows) below that.
 *
 * Card sources per tab, in order:
 *   1. cards already in the grid (active tab's first page)
 *   2. <template data-pcarg-source="N"> (rest of the server-rendered batch)
 *   3. /collections/<handle>?view=ic-cards&page=N (templates/collection.ic-cards.liquid)
 * Pooled cards stay inert (template content / DOMParser document) until they
 * are shown, so they never request images early. Cards are deduped by product
 * handle across all three sources.
 */
(function () {
  if (customElements.get('pcar-grid-tabs')) return;

  var STAGGER_MS = 40;
  var STAGGER_CAP = 7;
  var ROW_SHOW_AFTER = 48; // mobile-search-drawer.js SHOW_AFTER
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mobileQuery = window.matchMedia('(max-width: 989px)');

  function shuffle(list) {
    for (var i = list.length - 1; i > 0; i -= 1) {
      var j = Math.floor(Math.random() * (i + 1));
      var swap = list[i];
      list[i] = list[j];
      list[j] = swap;
    }
    return list;
  }

  function productKey(card) {
    var link = card.querySelector('a.ds-product-card__link[href]') || card.querySelector('a[href*="/products/"]');
    if (!link) return null;
    var match = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
    return match ? match[1] : link.getAttribute('href');
  }

  class PcarGridTabs extends HTMLElement {
    connectedCallback() {
      if (this.state) return;

      this.basePageSize = Math.max(1, parseInt(this.dataset.pageSize, 10) || 8);
      // The bar is this element's first child, so this element's top edge is
      // where the bar sits when it is not stuck.
      this.sentinel = this;
      this.bar = this.querySelector('[data-pcarg-bar]');
      this.rail = this.querySelector('[data-pcarg-rail]');
      this.panel = this.querySelector('[data-pcarg-panel]');
      this.grid = this.querySelector('[data-pcarg-grid]');
      this.shopAll = this.querySelector('[data-pcarg-shop-all]');
      this.shopAllLabel = this.querySelector('[data-pcarg-shop-all-label]');
      this.buttons = Array.prototype.slice.call(this.querySelectorAll('[role="tab"]'));
      if (!this.grid || !this.buttons.length) return;

      this.state = {};
      this.token = 0;
      this.buttons.forEach((button) => {
        var index = button.dataset.tab;
        this.state[index] = {
          index: index,
          button: button,
          label: button.dataset.label || button.textContent.trim(),
          url: button.dataset.url,
          total: parseInt(button.dataset.total, 10) || 0,
          shown: [],
          pool: [],
          seen: new Set(),
          sourceTaken: false,
          shuffle: button.hasAttribute('data-shuffle'),
          nextPage: 1,
          done: false,
          fetching: null,
        };
      });

      var activeButton = this.buttons.find((b) => b.getAttribute('aria-selected') === 'true') || this.buttons[0];
      this.active = activeButton.dataset.tab;
      var tab = this.state[this.active];
      Array.prototype.forEach.call(this.grid.querySelectorAll(':scope > .pcar-card'), (card) => {
        tab.shown.push(card);
        var key = productKey(card);
        if (key) tab.seen.add(key);
      });
      this.takeSource(tab);
      if (tab.shuffle) this.dealShuffled(tab);

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.rail.addEventListener('click', this.onClick);
      this.rail.addEventListener('keydown', this.onKeydown);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.onResize = this.onResize.bind(this);
      window.addEventListener('resize', this.onResize, { passive: true });
      this.updateStuck();
      this.syncCount();
    }

    /* Reorder one tab's preloaded cards. The active tab is dealt back into
       the grid (one page visible, the rest pooled) so each page load shows
       a different order. */
    dealShuffled(tab) {
      var all = tab.shown.concat(tab.pool);
      if (all.length < 2) return;
      shuffle(all);
      tab.shown.forEach((card) => card.remove());
      tab.shown = [];
      tab.pool = [];
      var target = this.pageSize;
      var fragment = document.createDocumentFragment();
      all.forEach((card, i) => {
        if (i < target) {
          fragment.appendChild(card);
          tab.shown.push(card);
        } else {
          tab.pool.push(card);
        }
      });
      this.grid.appendChild(fragment);
    }

    disconnectedCallback() {
      if (!this.state) return;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      window.removeEventListener('resize', this.onResize);
    }

    /* ── visible count ───────────────────────────────────────── */

    columns() {
      var value = window.getComputedStyle(this.grid).getPropertyValue('--pcarg-cols');
      return Math.max(1, parseInt(value, 10) || 1);
    }

    /* Desktop shows exactly two rows; smaller screens show the configured
       count rounded up to whole rows. */
    get pageSize() {
      var cols = this.columns();
      if (!mobileQuery.matches) return cols * 2;
      return Math.ceil(this.basePageSize / cols) * cols;
    }

    /* Keep the active tab at exactly one page (first paint, tab switch,
       column change). Extra cards go back to the front of the pool. */
    syncCount() {
      var tab = this.state[this.active];
      if (!tab || !tab.shown.length) return;
      var target = this.pageSize;
      if (tab.shown.length > target) {
        var extra = tab.shown.splice(target);
        extra.forEach((card) => card.remove());
        tab.pool = extra.concat(tab.pool);
        return;
      }
      if (tab.shown.length === target) return;
      var token = this.token;
      this.takeSource(tab);
      var show = () => {
        if (token !== this.token) return;
        var missing = this.pageSize - tab.shown.length;
        if (missing > 0) this.showFromPool(tab, missing);
      };
      if (tab.pool.length >= target - tab.shown.length || tab.done) show();
      else this.fill(tab).then(show);
    }

    onResize() {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => this.syncCount(), 150);
    }

    /* ── sources ─────────────────────────────────────────────── */

    takeSource(tab) {
      if (tab.sourceTaken) return;
      tab.sourceTaken = true;
      var template = this.querySelector('template[data-pcarg-source="' + tab.index + '"]');
      if (template) {
        Array.prototype.forEach.call(template.content.children, (card) => {
          if (card.classList.contains('pcar-card')) this.addToPool(tab, card);
        });
        template.remove();
        if (tab.shuffle) shuffle(tab.pool);
      }
      if (tab.total && tab.seen.size >= tab.total) tab.done = true;
    }

    addToPool(tab, card) {
      var key = productKey(card);
      if (!key || tab.seen.has(key)) return false;
      tab.seen.add(key);
      tab.pool.push(card);
      return true;
    }

    fetchNext(tab) {
      if (tab.done) return Promise.resolve();
      if (tab.fetching) return tab.fetching;

      var url;
      try {
        url = new URL(tab.url, window.location.origin);
      } catch (error) {
        tab.done = true;
        return Promise.resolve();
      }
      url.searchParams.set('view', 'ic-cards');
      url.searchParams.set('page', tab.nextPage);

      tab.fetching = fetch(url.toString(), { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then((html) => {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var wrapper = doc.querySelector('[data-ic-cards]');
          if (!wrapper) {
            tab.done = true;
            return;
          }
          var cards = wrapper.querySelectorAll(':scope > .pcar-card');
          Array.prototype.forEach.call(cards, (card) => this.addToPool(tab, card));
          if (tab.shuffle) shuffle(tab.pool);
          var pages = parseInt(wrapper.dataset.pages, 10) || 0;
          tab.nextPage += 1;
          if (!cards.length || tab.nextPage > pages) tab.done = true;
        })
        .catch(() => {
          tab.done = true;
        })
        .finally(() => {
          tab.fetching = null;
        });
      return tab.fetching;
    }

    /* Fetch until the pool can fill one page (or the collection runs out). */
    fill(tab) {
      if (tab.pool.length >= this.pageSize || tab.done) return Promise.resolve();
      return this.fetchNext(tab).then(() => this.fill(tab));
    }

    /* ── rendering ───────────────────────────────────────────── */

    animateIn(cards) {
      if (reducedMotion.matches) return;
      cards.forEach((card, i) => {
        card.style.setProperty('--pcarg-delay', Math.min(i, STAGGER_CAP) * STAGGER_MS + 'ms');
        card.classList.add('pcarg-enter');
        card.addEventListener(
          'animationend',
          () => {
            card.classList.remove('pcarg-enter');
            card.style.removeProperty('--pcarg-delay');
          },
          { once: true }
        );
      });
    }

    showFromPool(tab, count) {
      var added = [];
      var fragment = document.createDocumentFragment();
      while (added.length < count && tab.pool.length) {
        var card = document.adoptNode(tab.pool.shift());
        fragment.appendChild(card);
        added.push(card);
      }
      this.grid.appendChild(fragment);
      tab.shown = tab.shown.concat(added);
      this.animateIn(added);
      if (added.length) {
        document.dispatchEvent(new CustomEvent('pcar:cards-mounted', { detail: { root: this.grid } }));
      }
      return added;
    }

    showSkeletons() {
      var fragment = document.createDocumentFragment();
      for (var i = 0; i < this.pageSize; i += 1) {
        var skeleton = document.createElement('div');
        skeleton.className = 'pcarg-skeleton';
        skeleton.setAttribute('aria-hidden', 'true');
        skeleton.innerHTML =
          '<span class="pcarg-skeleton__media"></span>' +
          '<span class="pcarg-skeleton__line"></span>' +
          '<span class="pcarg-skeleton__line pcarg-skeleton__line--short"></span>';
        fragment.appendChild(skeleton);
      }
      this.grid.appendChild(fragment);
    }

    clearSkeletons() {
      this.grid.querySelectorAll(':scope > .pcarg-skeleton').forEach((node) => node.remove());
    }

    setBusy(busy) {
      this.panel.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    /* ── tabs ────────────────────────────────────────────────── */

    select(index, options) {
      var opts = options || {};
      var tab = this.state[index];
      if (!tab || index === this.active) return;

      var previous = this.state[this.active];
      this.active = index;
      var token = (this.token += 1);

      this.buttons.forEach((button) => {
        var selected = button === tab.button;
        button.setAttribute('aria-selected', selected ? 'true' : 'false');
        button.tabIndex = selected ? 0 : -1;
      });
      if (opts.focus) tab.button.focus({ preventScroll: true });
      this.panel.setAttribute('aria-labelledby', tab.button.id);
      if (this.shopAll) {
        if (this.shopAllLabel) this.shopAllLabel.textContent = 'Shop All ' + tab.label;
        if (tab.url) this.shopAll.setAttribute('href', tab.url);
      }
      this.centerPill(tab.button);

      // Lock the grid height so swapping cards never collapses the page.
      this.grid.style.minHeight = this.grid.offsetHeight + 'px';
      previous.shown.forEach((card) => card.remove());
      this.clearSkeletons();
      this.scrollBack();

      this.takeSource(tab);
      var render = () => {
        if (token !== this.token) return;
        this.clearSkeletons();
        if (tab.shown.length) {
          var fragment = document.createDocumentFragment();
          tab.shown.forEach((card) => fragment.appendChild(card));
          this.grid.appendChild(fragment);
          this.animateIn(tab.shown.slice(0, STAGGER_CAP + 1));
        } else {
          this.showFromPool(tab, this.pageSize);
        }
        this.setBusy(false);
        this.syncCount();
        window.requestAnimationFrame(() => {
          if (token === this.token) this.grid.style.minHeight = '';
        });
      };

      if (tab.shown.length || tab.pool.length >= this.pageSize || tab.done) {
        render();
      } else {
        this.showSkeletons();
        this.setBusy(true);
        this.fill(tab).then(render);
      }
    }

    onClick(event) {
      var button = event.target.closest('[role="tab"]');
      if (!button || !this.rail.contains(button)) return;
      this.select(button.dataset.tab);
    }

    onKeydown(event) {
      var current = this.buttons.indexOf(document.activeElement);
      if (current === -1) return;
      var next = null;
      switch (event.key) {
        case 'ArrowRight':
          next = (current + 1) % this.buttons.length;
          break;
        case 'ArrowLeft':
          next = (current - 1 + this.buttons.length) % this.buttons.length;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = this.buttons.length - 1;
          break;
        default:
          return;
      }
      event.preventDefault();
      var button = this.buttons[next];
      button.focus({ preventScroll: true });
      this.select(button.dataset.tab, { focus: true });
    }

    centerPill(button) {
      if (!this.rail) return;
      var railRect = this.rail.getBoundingClientRect();
      var rect = button.getBoundingClientRect();
      if (rect.left >= railRect.left && rect.right <= railRect.right) return;
      var left = this.rail.scrollLeft + (rect.left - railRect.left) - (railRect.width - rect.width) / 2;
      this.rail.scrollTo({ left: left, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    /* If the reader is down inside the grid, bring the grid start back to just
       under the sticky bar. Leave the page alone if the bar's own spot is in view. */
    scrollBack() {
      if (!this.sentinel || !this.bar || this.bar.hidden) return;
      var stickyTop = parseFloat(window.getComputedStyle(this.bar).top) || 0;
      var naturalTop = this.sentinel.getBoundingClientRect().top;
      if (naturalTop >= stickyTop - 1) return;

      // Scrolling up far enough brings the mobile search row back, which pushes
      // the bar down by the row's height; aim for where the bar will settle.
      var header = document.querySelector('.section-header');
      if (
        mobileQuery.matches &&
        header &&
        header.classList.contains('is-msearch-hidden') &&
        stickyTop - naturalTop > ROW_SHOW_AFTER
      ) {
        var rowHeight = parseFloat(
          window.getComputedStyle(document.documentElement).getPropertyValue('--ic-msearch-h')
        );
        if (rowHeight) stickyTop += rowHeight;
      }

      window.scrollTo({
        top: window.pageYOffset + naturalTop - stickyTop,
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    /* ── stuck hairline ──────────────────────────────────────── */

    onScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = 0;
        this.updateStuck();
      });
    }

    updateStuck() {
      if (!this.sentinel || !this.bar || this.bar.hidden) return;
      var stickyTop = parseFloat(window.getComputedStyle(this.bar).top) || 0;
      this.bar.classList.toggle('is-stuck', this.sentinel.getBoundingClientRect().top < stickyTop - 0.5);
    }
  }

  customElements.define('pcar-grid-tabs', PcarGridTabs);
})();
